import IconChevronRightSrc from "@/assets/images/icon-chevron-right@2x.png"
import { Spacer } from "@/components/spacer"
import { ITestimonial, Testimonial } from "@/components/testimonial"
import { useWindowSize } from "@/utilities/helpers"
import React, { FC, useRef, useState } from "react"
import { up } from "styled-breakpoints"
import styled, { css } from "styled-components"
import { Autoplay, Navigation } from "swiper"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"

export interface TestimonialsProps {
  data?: Array<ITestimonial>
}

const SwiperWrapper = styled(Swiper)<any>`
  position: relative;
  align-items: stretch;
  width: 100%;
  user-select: none;
  opacity: 0;
  transition: opacity 200ms ease;

  ${up("md")} {
    width: calc(100% - 122px);
    margin: 0;
  }

  /* Gradient overlays */
  &::before,
  &::after {
    position: absolute;
    top: 0;
    z-index: 2;
    display: block;
    width: 120px;
    height: 100%;
    pointer-events: none;
    content: "";

    ${up("sm")} {
      width: 285px;
    }
  }

  &::before {
    left: 0;
    background: linear-gradient(90deg, #020416 5%, rgba(2, 4, 22, 0) 50%);
    opacity: 0;

    ${up("md")} {
      opacity: 1;
    }
  }

  &::after {
    right: 0;
    background: linear-gradient(270deg, #020416 5%, rgba(2, 4, 22, 0) 50%);
  }

  /* Fade in after loading (prevents FOUCing it up) */
  ${({ isVisible }) =>
    isVisible &&
    css`
      opacity: 1;
    `}
`
const StyledSwiperSlide = styled(SwiperSlide)`
  display: flex;
  align-items: stretch;
  width: 340px;
  height: 100%;
  min-height: 310px;
`
const StyledNav = styled.div`
  display: flex;
  gap: 0 8px;
  justify-content: center;
`
const NavButtonStyle = css`
  display: block;
  width: 40px;
  height: 40px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.1) url("${IconChevronRightSrc}") no-repeat
    center center;
  background-size: 20px 20px;
  border-radius: 40px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  transition: all 200ms ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`
const NavigationPrev = styled.div`
  ${NavButtonStyle}
`
const NavigationNext = styled.div`
  ${NavButtonStyle}

  transform: rotate(180deg);
`

export const Testimonials: FC<TestimonialsProps> = ({ data }) => {
  if (!data) return null

  // Refs
  const navigationPrevRef = useRef<HTMLDivElement>(null)
  const navigationNextRef = useRef<HTMLDivElement>(null)

  // Viewport size check
  const { width: windowSize } = useWindowSize()

  // Reveal on appear
  const [isVisible, setIsVisible] = useState(false)

  // Repeat data so the loop function works when # of items is too small to ensure a good looping effect
  // src: https://swiperjs.com/swiper-api#param-loop
  const visibleSlides = windowSize < 768 ? 1 : 3
  const slides =
    data.length >= visibleSlides * 2 + 1
      ? [...data]
      : [...data, ...data, ...data]

  return (
    <SwiperWrapper
      autoplay={{
        delay: 2000,
        disableOnInteraction: false,
      }}
      navigation={{
        nextEl: navigationNextRef.current,
        prevEl: navigationPrevRef.current,
      }}
      onInit={(swiper: any) => {
        swiper.params.navigation.prevEl = navigationPrevRef.current
        swiper.params.navigation.nextEl = navigationNextRef.current
        swiper.navigation.init()
        swiper.navigation.update()
        setIsVisible(true)
      }}
      centeredSlides
      grabCursor
      isVisible={isVisible}
      loop
      modules={[Autoplay, Navigation]}
      slidesPerView={visibleSlides}
      spaceBetween={32}
      speed={400}>
      {slides.map((testimonial: ITestimonial, index: number) => (
        <StyledSwiperSlide key={`testimonial-${index}`}>
          <Testimonial data={testimonial} />
        </StyledSwiperSlide>
      ))}
      <Spacer height={{ default: "40px" }} />
      <StyledNav>
        <NavigationPrev ref={navigationPrevRef} />
        <NavigationNext ref={navigationNextRef} />
      </StyledNav>
    </SwiperWrapper>
  )
}
